import { rgba } from 'polished';
import { Droppable } from 'react-beautiful-dnd';
import { Box } from 'reflexbox';
import styled from 'styled-components';

import { useLocalize } from '@treams/treams-hooks';
import * as graphql from '@treams/treams-network';
import { Paragraph, tokens } from '@treams/treams-ui';

import PopoverTarget from '../packages/kakapo-ui-kit/components/popovers/PopoverTarget';
import TalentReviewLabel from './TalentReviewLabel';
import TalentReviewPerson from './TalentReviewPerson';

const CellToolbar = styled(Box)`
	position: absolute;
	left: 50%;
	top: 100%;
	transform: translateX(-50%) translateY(-50%);
`;

const CellToolbarDown = styled(Box)`
	height: 50%;
	width: 100%;
	bottom: 0;
	background: white;
	position: absolute;
`;

const CellOutline = styled(Box)<{ hex: string; hover?: boolean }>`
	border-radius: 6px;
	border: 2px solid ${(props) => rgba(props.hex, 0.5)};
	background: ${tokens.surface.secondary};
	height: 140px;
	position: relative;
	color: ${(props) => (props.hover ? props.theme.colors.primary : props.hex)};

	${(props) =>
		props.hover &&
		`
	background: ${rgba(props.theme.colors.primary, 0.1)};
	border-color: ${rgba(props.theme.colors.primary, 0.5)};
`}
`;

const CellContainer = styled(Box)<{ hex: string; hover?: boolean }>`
	flex-grow: 0;
	flex-shrink: 0;
	width: 156px;
	height: 140px;
	position: relative;
	overflow-x: hidden;
	overflow-y: auto;
`;

const Footnote = styled(Paragraph)`
	display: -webkit-box;
	-webkit-line-clamp: 2;
	-webkit-box-orient: vertical;
	text-align: center;
	margin-top: 4px;
	width: 156px;
	line-height: 20px;
	max-height: 40px;
	text-overflow: ellipsis;
	overflow: hidden;
	position: relative;
`;

export default function TalentReviewCell(props: {
	row: number;
	column: number;
	readonly?: boolean;
	dragging?: boolean;
	cell: graphql.TalentReviewTemplateCell;
	users: graphql.User[];
	temporaryPlacements: graphql.TalentReviewPlacement[];
	onUpdateMotivation: (userID: string, motivation: string) => void;
}) {
	const { localize } = useLocalize();

	const instruction = localize(props.cell.instruction);

	const hex = `#${props.cell.color.toString(16).padStart(6, '0')}`;

	return (
		<Box marginLeft={10}>
			<Droppable
				droppableId={`cell-${props.row}-${props.column}`}
				type="USER"
				isDropDisabled={props.readonly}
			>
				{(provided, snapshot) => (
					<CellOutline
						ref={provided.innerRef}
						{...provided.droppableProps}
						hex={hex}
						hover={snapshot.isDraggingOver}
					>
						{localize(props.cell.label)?.text?.length ? (
							<CellToolbar>
								<CellToolbarDown />
								<Box style={{ position: 'relative' }}>
									<TalentReviewLabel text={props.cell.label} />
								</Box>
							</CellToolbar>
						) : undefined}
						<CellContainer hex={hex} hover={snapshot.isDraggingOver}>
							{props.temporaryPlacements
								.filter(({ row, column }) => row === props.row && column === props.column)
								.map(({ user, motivation }, index) => (
									<TalentReviewPerson
										key={user.id}
										user={user}
										index={index}
										placed
										small
										motivation={motivation ?? undefined}
										onUpdateMotivation={(motivation) =>
											props.onUpdateMotivation(user.id, motivation)
										}
										readonly={props.readonly}
									/>
								))}
							{provided.placeholder}
						</CellContainer>
					</CellOutline>
				)}
			</Droppable>
			{instruction && (
				<PopoverTarget
					enabled={!props.dragging}
					content={
						<Box p={3}>
							<Paragraph>{instruction.text}</Paragraph>
						</Box>
					}
				>
					<Footnote>{instruction.text}</Footnote>
				</PopoverTarget>
			)}
		</Box>
	);
}
